import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Card, 
  Row, 
  Col, 
  Tag, 
  Table, 
  Space, 
  Button, 
  Popconfirm, 
  Typography, 
  Collapse,
  Spin,
  Descriptions,
  message
} from 'antd';
import { 
  DeleteOutlined, 
  ClearOutlined, 
  FormOutlined, 
  ShoppingOutlined, 
  DollarOutlined, 
  PieChartOutlined,
  DownOutlined,
  DatabaseOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  NumberOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  BoxOutlined,
  LineChartOutlined,
  GroupOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import StockApi from '../../Apis/StockApi';
import ProductApi from '../../Apis/ProductApi';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const StorageDetailModal = ({ 
  visible, 
  onClose, 
  selectedStorage,
  productStockSummaryList = [], 
  onDeleteStock,
  decodedToken,
  onWasteStock,
  onAdjustStock,
  refreshTrigger = 0,
  onRefreshStorage // New prop to refresh the parent data
}) => {
  const [expandedGroups, setExpandedGroups] = useState({});
  const [productStocks, setProductStocks] = useState({});
  const [groupStocks, setGroupStocks] = useState({});
  const [productDetails, setProductDetails] = useState({});
  const [loading, setLoading] = useState({});
  const [groupLoading, setGroupLoading] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [stocksPageSize] = useState(5);
  const [refreshingGroup, setRefreshingGroup] = useState(null);
  const [deletingStock, setDeletingStock] = useState(null);
  const [groupExpiryStatus, setGroupExpiryStatus] = useState({});
  const [groupExpiryLoading, setGroupExpiryLoading] = useState({});

  const CURRENCY = decodedToken?.currency === "USD" ? "$" : "€";

    // Function to check if any stock in a group is expired or about to expire
const checkGroupExpiryStatus = useCallback(async (groupId, group) => {
    if (!selectedStorage || !groupId || !group) return;
    
    // Skip if already checked
    if (groupExpiryStatus[groupId] !== undefined) return;
    
    setGroupExpiryLoading(prev => ({ ...prev, [groupId]: true }));
    
    try {
        let hasExpiredStock = false;
        let hasExpiringStock = false;
        let earliestExpiryDate = null;
        let totalStockItems = 0;
        let totalValidStock = 0;
        
        // Check each product in the group
        for (const product of group.products) {
            const productId = product.productId;
            
            try {
                const response = await StockApi.ListStockProduct(productId);
                const stocks = response.payload || [];
                
                if (stocks.length > 0) {
                    // Filter valid stock (quantity > 0.01)
                    const validStocks = stocks.filter(stock => 
                        parseFloat(stock.unitOfMeasurement1 || 0) >= 0.01
                    );
                    
                    totalValidStock += validStocks.length;
                    
                    for (const stock of validStocks) {
                        totalStockItems++;
                        
                        if (stock.expiryDate) {
                            const daysDiff = calculateDaysDiff(stock.expiryDate);
                            
                            if (daysDiff !== null) {
                                // Check for expired stock
                                if (daysDiff < 0) {
                                    hasExpiredStock = true;
                                }
                                
                                // Check for expiring soon (within 5 days)
                                if (daysDiff >= 0 && daysDiff <= 5) {
                                    hasExpiringStock = true;
                                }
                                
                                // Track earliest expiry date
                                if (!earliestExpiryDate || 
                                    (daysDiff !== null && daysDiff < calculateDaysDiff(earliestExpiryDate))) {
                                    earliestExpiryDate = stock.expiryDate;
                                }
                            }
                        }
                    }
                }
            } catch (productError) {
                console.error(`Error checking stock for product ${productId}:`, productError);
                continue;
            }
        }
        
        // Determine overall status
        let status = 'ok'; // Default: all good
        let severity = 0; // 0=ok, 1=expiring, 2=expired
        
        if (hasExpiredStock) {
            status = 'expired';
            severity = 2;
        } else if (hasExpiringStock) {
            status = 'expiring';
            severity = 1;
        }
        
        setGroupExpiryStatus(prev => ({
            ...prev,
            [groupId]: {
                status,
                severity,
                hasExpiredStock,
                hasExpiringStock,
                earliestExpiryDate,
                totalStockItems,
                totalValidStock
            }
        }));
        
    } catch (error) {
        console.error(`Error checking expiry status for group ${groupId}:`, error);
        setGroupExpiryStatus(prev => ({
            ...prev,
            [groupId]: {
                status: 'unknown',
                severity: 0,
                hasExpiredStock: false,
                hasExpiringStock: false,
                earliestExpiryDate: null,
                totalStockItems: 0,
                totalValidStock: 0
            }
        }));
    } finally {
        setGroupExpiryLoading(prev => ({ ...prev, [groupId]: false }));
    }
}, [selectedStorage, groupExpiryStatus]);

  // Auto-check expiry status for all groups when modal opens
useEffect(() => {
    if (!visible || !productStockSummaryList || productStockSummaryList.length === 0) return;
    
    const grouped = groupProductsByCode(productStockSummaryList);
    
    // Check expiry status for each group
    grouped.forEach(group => {
        if (groupExpiryStatus[group.groupId] === undefined) {
            checkGroupExpiryStatus(group.groupId, group);
        }
    });
}, [visible, productStockSummaryList, checkGroupExpiryStatus]);


  const getTemperatureCategory = (isHot) => {
    if (isHot) return { 
      category: "Hot Storage", 
      color: "#ff4d4f"
    };
    else return { 
      category: "Cold Storage", 
      color: "#1890ff"
    };
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    
    let date;
    try {
      if (typeof dateValue === 'number' || (typeof dateValue === 'string' && /^\d+$/.test(dateValue))) {
        if (dateValue > 1000000000000) {
          date = new Date(parseInt(dateValue, 10));
        } else {
          date = new Date(parseInt(dateValue, 10) * 1000);
        }
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else {
        return "Invalid date";
      }
      
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      
      return date.toLocaleDateString("en-US", { 
        day: "2-digit", 
        month: "short", 
        year: "numeric" 
      });
    } catch (error) {
      console.error("Error formatting date:", error, dateValue);
      return "Invalid date";
    }
  };

  const calculateDaysDiff = (dateValue) => {
    if (!dateValue) return null;
    
    try {
      let date;
      if (typeof dateValue === 'number' || (typeof dateValue === 'string' && /^\d+$/.test(dateValue))) {
        if (dateValue > 1000000000000) {
          date = new Date(parseInt(dateValue, 10));
        } else {
          date = new Date(parseInt(dateValue, 10) * 1000);
        }
      } else {
        date = new Date(dateValue);
      }
      
      if (isNaN(date.getTime())) return null;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      
      const timeDiff = date.getTime() - today.getTime();
      return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    } catch (error) {
      console.error("Error calculating days diff:", error);
      return null;
    }
  };

    // Check if stock is expired
  const isStockExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const daysDiff = calculateDaysDiff(expiryDate);
    return daysDiff !== null && daysDiff < 0;
  };

  const extractGroupId = (productCode) => {
    if (!productCode) return null;
    const match = productCode.match(/#(\d+)$/);
    if (match) {
      return match[1];
    }
    return null;
  };

  const getBaseName = (productCode) => {
    if (!productCode) return 'Unknown Product';
    const base = productCode.split('#')[0].trim();
    return base.replace(/_+$/, '');
  };

  const groupProductsByCode = (products) => {
    const groups = new Map();
    
    products.forEach(product => {
      const groupId = extractGroupId(product.productCode);
      
      if (groupId) {
        if (!groups.has(groupId)) {
          groups.set(groupId, {
            groupId: groupId,
            baseName: getBaseName(product.productCode),
            groupProductName: product.productName || product.productCode || 'Unknown Product',
            products: [],
            totalQuantity: 0,
            totalPrice: 0,
            productIds: [],
            units: [],
            productCodes: []
          });
        }
        
        const group = groups.get(groupId);
        group.products.push(product);
        group.productIds.push(product.productId);
        group.totalQuantity += parseFloat(product.totalQuantity) || 0;
        group.totalPrice += parseFloat(product.totalPrice) || 0;
        if (product.unit && !group.units.includes(product.unit)) {
          group.units.push(product.unit);
        }
        if (product.productCode && !group.productCodes.includes(product.productCode)) {
          group.productCodes.push(product.productCode);
        }
      } else {
        const uniqueId = `individual_${product.productId}`;
        if (!groups.has(uniqueId)) {
          groups.set(uniqueId, {
            groupId: uniqueId,
            baseName: product.productName || product.productCode || 'Unknown Product',
            groupProductName: product.productName || product.productCode || 'Unknown Product',
            products: [product],
            totalQuantity: parseFloat(product.totalQuantity) || 0,
            totalPrice: parseFloat(product.totalPrice) || 0,
            productIds: [product.productId],
            units: [product.unit].filter(Boolean),
            productCodes: [product.productCode].filter(Boolean)
          });
        }
      }
    });
    
    return Array.from(groups.values());
  };

  const calculateOverallSummary = () => {
    if (!productStockSummaryList || productStockSummaryList.length === 0) {
      return {
        totalProducts: 0,
        totalQuantity: 0,
        totalValue: 0,
        uniqueProducts: 0,
        totalGroups: 0
      };
    }

    const grouped = groupProductsByCode(productStockSummaryList);
    
    const summary = {
      totalProducts: productStockSummaryList.length,
      totalQuantity: 0,
      totalValue: 0,
      uniqueProducts: new Set(),
      totalGroups: grouped.length
    };

    grouped.forEach(group => {
      group.productIds.forEach(id => summary.uniqueProducts.add(id));
      summary.totalQuantity += group.totalQuantity;
      summary.totalValue += group.totalPrice;
    });

    return {
      totalProducts: summary.totalProducts,
      uniqueProductCount: summary.uniqueProducts.size,
      totalQuantity: summary.totalQuantity.toFixed(2),
      totalValue: summary.totalValue.toFixed(2),
      totalGroups: summary.totalGroups
    };
  };

  const fetchProductDetails = async (productId) => {
    if (!productId) return;
    
    setDetailLoading(prev => ({ ...prev, [productId]: true }));
    try {
      const response = await ProductApi.SingleProducts(productId);
      setProductDetails(prev => ({
        ...prev,
        [productId]: response.payload || response
      }));
    } catch (error) {
      console.error(`Error fetching product details for ${productId}:`, error);
      setProductDetails(prev => ({
        ...prev,
        [productId]: null
      }));
    } finally {
      setDetailLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const fetchGroupStocks = async (groupId, group, forceRefresh = false) => {
    if (!selectedStorage || !groupId) return;
    
    // If not forcing refresh and we already have stocks, skip
    if (!forceRefresh && groupStocks[groupId]) {
      return;
    }
    
    setGroupLoading(prev => ({ ...prev, [groupId]: true }));
    
    try {
      const allStocks = [];
      let totalItems = 0;
      let hasError = false;
      
      for (const product of group.products) {
        const productId = product.productId;
        
        try {
          await fetchProductDetails(productId);
          
          const response = await StockApi.ListStockProduct(productId);
          const stocks = response.payload || [];
          
          if (stocks.length > 0) {
            const stocksWithProductInfo = stocks.map(stock => ({
              ...stock,
              productCode: product.productCode,
              productName: product.productName,
              productId: productId
            }));
            
            allStocks.push(...stocksWithProductInfo);
            totalItems += stocks.length;
          }
        } catch (productError) {
          console.error(`Error fetching stocks for product ${productId} (${product.productCode}):`, productError);
          hasError = true;
          continue;
        }
      }
      
      setGroupStocks(prev => ({
        ...prev,
        [groupId]: {
          stocks: allStocks,
          total: totalItems,
          currentPage: 1,
          hasError: hasError
        }
      }));
      
    } catch (error) {
      console.error(`Error fetching group stocks for ${groupId}:`, error);
      setGroupStocks(prev => ({
        ...prev,
        [groupId]: {
          stocks: [],
          total: 0,
          currentPage: 1,
          hasError: true
        }
      }));
    } finally {
      setGroupLoading(prev => ({ ...prev, [groupId]: false }));
    }
  };

  // Refresh a specific group's stocks
  const refreshGroupStocks = async (groupId) => {
    const grouped = groupProductsByCode(productStockSummaryList);
    const group = grouped.find(g => g.groupId === groupId);
    
  };


  // Handle waste action with refresh
  const handleWasteStock = async (productId, stockId) => {
    try {
      await onWasteStock(productId, stockId);
      
      // Find which group this product belongs to
      const grouped = groupProductsByCode(productStockSummaryList);
      let groupId = null;
      
      for (const group of grouped) {
        if (group.productIds.includes(productId)) {
          groupId = group.groupId;
          break;
        }
      }
      
      // Refresh the group's stocks if it's expanded
      if (groupId && expandedGroups[groupId]) {
        await refreshGroupStocks(groupId);
      }
      
      // Refresh the parent data
      if (onRefreshStorage) {
        await onRefreshStorage();
      }
      
    } catch (error) {
      console.error('Error during waste operation:', error);
      message.error('Failed to waste stock. Please try again.');
    }
  };

  // Handle adjust action with refresh
  const handleAdjustStock = async (productId, stockId, productPayload) => {
    try {
      await onAdjustStock(productId, stockId, productPayload);
      
      // Find which group this product belongs to
      const grouped = groupProductsByCode(productStockSummaryList);
      let groupId = null;
      
      for (const group of grouped) {
        if (group.productIds.includes(productId)) {
          groupId = group.groupId;
          break;
        }
      }
      
      // Refresh the group's stocks if it's expanded
      if (groupId && expandedGroups[groupId]) {
        await refreshGroupStocks(groupId);
      }
      
      // Refresh the parent data
      if (onRefreshStorage) {
        await onRefreshStorage();
      }
      
    } catch (error) {
      console.error('Error during adjust operation:', error);
      message.error('Failed to adjust stock. Please try again.');
    }
  };

  const handleGroupExpand = (groupId, expanded) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: expanded
    }));
    
    if (expanded) {
      const grouped = groupProductsByCode(productStockSummaryList);
      const group = grouped.find(g => g.groupId === groupId);
      
      if (group) {
        fetchGroupStocks(groupId, group);
      }
    }
  };

  const handleProductPageChange = (groupId, pageNum) => {
    setGroupStocks(prev => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        currentPage: pageNum
      }
    }));
  };

  // Reset expanded state when modal closes
  useEffect(() => {
    if (!visible) {
      setExpandedGroups({});
      setGroupStocks({});
      setProductDetails({});
      setLoading({});
      setGroupLoading({});
      setDetailLoading({});
      setRefreshingGroup(null);
      setDeletingStock(null);
    }
  }, [visible]);

  // Refresh expanded groups when refreshTrigger changes
  useEffect(() => {
    if (!visible || !refreshTrigger) return;

    const refreshExpandedGroups = async () => {
      const expandedGroupIds = Object.keys(expandedGroups).filter(key => expandedGroups[key]);
      if (expandedGroupIds.length === 0) return;

      setGroupStocks({});
      setProductDetails({});
      setGroupLoading({});

      const grouped = groupProductsByCode(productStockSummaryList);
      
      for (const groupId of expandedGroupIds) {
        const group = grouped.find(g => g.groupId === groupId);
        if (group) {
          await fetchGroupStocks(groupId, group, true);
        }
      }
    };

    refreshExpandedGroups();
  }, [visible, refreshTrigger]);

  const isHotStorage = selectedStorage?.temperatureMinInCelsius === 0 && 
                      selectedStorage?.temperatureMaxInCelsius === 0;
  const tempCategory = getTemperatureCategory(isHotStorage);

  const getUOMInfo = (productId) => {
    const productDetail = productDetails[productId];
    const productSummary = productStockSummaryList.find(p => p.productId === productId);
    
    if (productDetail) {
      return {
        uomOne: productDetail.unitUomOne || productDetail.uom1 || productDetail.primaryUnit || 'UOM1',
        uomTwo: productDetail.unitUomTwo || productDetail.uom2 || productDetail.secondaryUnit || 'UOM2',
        conversion: productDetail.unitOfMeasurement2 || productDetail.conversionFactor || 1,
        unitCost: productDetail.costPrice || productDetail.unitCost || productSummary?.avgPrice || 0,
        costPriceUoM2: productDetail.costPriceUoM2 || 0,
        unitOfMeasurement1: productDetail.unitOfMeasurement1 || 1,
        unitOfMeasurement2: productDetail.unitOfMeasurement2 || 1,
        unitOfMeasurement3: productDetail.unitOfMeasurement3 || 1
      };
    }
    
    if (productSummary) {
      return {
        uomOne: productSummary.unitUomOne || 'UOM1',
        uomTwo: productSummary.unitUomTwo || 'UOM2',
        conversion: productSummary.unitOfMeasurement2 || 1,
        unitCost: productSummary.avgPrice || 0
      };
    }
    
    return {
      uomOne: 'UOM1',
      uomTwo: 'UOM2',
      conversion: 1,
      unitCost: 0
    };
  };

  const getGroupUOMInfo = (group) => {
    if (group && group.productIds && group.productIds.length > 0) {
      return getUOMInfo(group.productIds[0]);
    }
    return {
      uomOne: 'UOM1',
      uomTwo: 'UOM2',
      conversion: 1,
      unitCost: 0
    };
  };

 const getGroupStockColumns = (group) => {
  return [
    {
      title: 'Product Variant',
      dataIndex: 'productName',
      key: 'productName',
      width: 180,
      render: (text, record) => (
        <div>
          <Tag color="cyan" style={{ marginBottom: '4px' }}>
            {text || 'N/A'}
          </Tag>
          <div style={{ fontSize: '11px', color: '#999' }}>
            {record.productCode || ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 120,
      render: (text) => (
        <Text strong>
          {text ? (
            <>
              <FileTextOutlined style={{ marginRight: '4px', color: '#1890ff' }} />
              {text}
            </>
          ) : (
            'N/A'
          )}
        </Text>
      ),
    },
    {
      title: 'Batch Code',
      dataIndex: 'batchCode',
      key: 'batchCode',
      width: 120,
      render: (text) => <Text strong>{text || 'N/A'}</Text>,
    },
    {
      title: 'OUBD',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 150,
      render: (dateValue) => {
        if (!dateValue) return <Text type="secondary">No expiry</Text>;
        
        const formattedDate = formatDate(dateValue);
        const daysDiff = calculateDaysDiff(dateValue);
        
        let status = 'default';
        let statusText = '';
        
        if (daysDiff !== null) {
          if (daysDiff < 0) {
            status = 'expired';
            statusText = 'Expired';
          } else if (daysDiff <= 5) {
            status = 'critical';
            statusText = `${daysDiff} days`;
          } else {
            statusText = `${daysDiff} days`;
          }
        }
        
        const statusColors = {
          expired: '#ff4d4f',
          critical: '#faad14',
          default: '#52c41a'
        };
        
        return (
          <div>
            <div style={{ 
              color: statusColors[status], 
              fontWeight: 'bold', 
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <CalendarOutlined style={{ marginRight: '4px' }} />
              {formattedDate}
            </div>
            {daysDiff !== null && (
              <div style={{ 
                fontSize: '12px', 
                marginTop: '2px',
                color: statusColors[status]
              }}>
                {statusText}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: `Qty (UOM1)`,
      dataIndex: 'initQuantity',
      key: 'quantityUOM1',
      width: 130,
      align: 'right',
      render: (_, record) => {
        // Get product-specific UOM info for this record
        const productUOMInfo = getUOMInfo(record.productId);
        const qty = parseFloat(record.unitOfMeasurement1 || 0);
        
        // Calculate UOM1 quantity
        const uom1Quantity = qty / (productUOMInfo.unitOfMeasurement2 || 1);
        
        return (
          <div>
            <Text strong style={{ fontSize: '14px' }}>
              {uom1Quantity.toFixed(2)}
            </Text>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {productUOMInfo.uomOne}
            </div>
          </div>
        );
      },
    },
    {
      title: `Qty2 (UOM2)`,
      dataIndex: 'initQuantity',
      key: 'quantityUOM2',
      width: 130,
      align: 'right',
      render: (_, record) => {
        // Get product-specific UOM info for this record
        const productUOMInfo = getUOMInfo(record.productId);
        const qty = parseFloat(record.unitOfMeasurement1 || 0);
        
        return (
          <div>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {qty.toFixed(2)}
            </Text>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {productUOMInfo.uomTwo}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Cost',
      key: 'unitCost',
      width: 120,
      align: 'right',
      render: (_, record) => {
        // Get product-specific UOM info for this specific stock item
        const productUOMInfo = getUOMInfo(record.productId);
        
        console.log(`💰 COST CALCULATION FOR STOCK ID: ${record.id}`);
        console.log(`📦 Product ID: ${record.productId}`);
        console.log(`📊 Stock API - unitOfMeasurement1:`, record.unitOfMeasurement1);
        console.log(`📊 Product API - costPriceUoM2:`, productUOMInfo.costPriceUoM2);
        
        // Get quantity from Stock API (unitOfMeasurement1)
        const quantity = parseFloat(record.unitOfMeasurement1) || 0;
        
        // Get cost per unit from Product API (costPriceUoM2)
        const costPerUnit = parseFloat(productUOMInfo.costPriceUoM2) || 0;
        
        // Calculate total cost: quantity * cost per unit
        const totalCost = quantity * costPerUnit;
        
        console.log(`🧮 Calculation: ${quantity} × ${costPerUnit} = ${totalCost}`);
        console.log(`✅ Final Cost: ${CURRENCY} ${totalCost.toFixed(2)}`);
        console.log('---');
        
        return (
          <div>
            <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '14px' }}>
              {CURRENCY} {totalCost.toFixed(2)}
            </div>
            
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      fixed: 'right',
      render: (_, record) => {
        const expired = isStockExpired(record.expiryDate);
        const isDeleting = deletingStock === record.id;
        
        return (
          <Space size="small" wrap>
            <Button 
              size="small" 
              type="primary" 
              onClick={() => handleWasteStock(record.productId, record.id)}
            >
              Waste
            </Button>
            {(decodedToken?.roles?.[0] === "MANAGER" || decodedToken?.roles?.[0] === "SUB_TENANT_ADMIN") && (
              <Button 
                size="small" 
                type="default" 
                disabled={expired}
                onClick={() => {
                  const productPayload =
                    productDetails[record.productId] ||
                    productStockSummaryList.find((item) => item.productId === record.productId) ||
                    null;

                  handleAdjustStock(record.productId, record.id, productPayload);
                }}
              >
                Adjust
              </Button>
            )}
          </Space>
        );
      },
    },
  ];
};

  const overallSummary = calculateOverallSummary();
  const groupedProducts = groupProductsByCode(productStockSummaryList);

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {selectedStorage?.name} - Stock Details
            <Tag 
              color={tempCategory.color}
              style={{ marginLeft: '12px', fontSize: '14px', padding: '4px 8px' }}
            >
              {tempCategory.category}
            </Tag>
          </Title>
          <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
            {selectedStorage?.location && <><strong>Location:</strong> {selectedStorage.location} | </>}
            <strong>Temperature:</strong> {isHotStorage 
              ? "N/A" 
              : `${selectedStorage?.temperatureMinInCelsius}°C - ${selectedStorage?.temperatureMaxInCelsius}°C`}
            {selectedStorage?.alertTrigger && (
              <Tag color="green" style={{ marginLeft: '12px' }}>Alerts Enabled</Tag>
            )}
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1400}
      style={{ top: 20 }}
      destroyOnClose
    >
      {/* Overall Summary Cards */}
      {groupedProducts.length > 0 && (
        <div style={{ 
          backgroundColor: '#fafafa', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '24px',
          border: '1px solid #f0f0f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <Title level={5} style={{ marginBottom: '16px', color: '#262626' }}>
            <PieChartOutlined style={{ marginRight: '8px' }} />
            Storage Overview
          </Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '28px', 
                    fontWeight: 'bold', 
                    color: '#0e5ef2',
                    marginBottom: '4px'
                  }}>
                    {overallSummary.totalGroups}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    <GroupOutlined style={{ marginRight: '4px' }} />
                    Products
                  </div>
                </div>
              </Card>
           
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '28px', 
                    fontWeight: 'bold', 
                    color: '#0e5ef2',
                    marginBottom: '4px'
                  }}>
                    {overallSummary.totalQuantity}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    <NumberOutlined style={{ marginRight: '4px' }} />
                    Total Quantity
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card size="small" hoverable>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ 
                    fontSize: '28px', 
                    fontWeight: 'bold', 
                    color: '#0e5ef2',
                    marginBottom: '4px'
                  }}>
                    {CURRENCY}{overallSummary.totalValue}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    <DollarOutlined style={{ marginRight: '4px' }} />
                    Total Value
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      )}

      {/* Product Groups Accordion */}
      <Title level={5} style={{ marginBottom: '16px', color: '#262626' }}>
        <GroupOutlined style={{ marginRight: '8px' }} />
        Products ({groupedProducts.length})
      </Title>
      
      {groupedProducts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <ShoppingOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
          <p>No products found in this storage</p>
        </div>
      ) : (
        <Collapse 
          activeKey={Object.keys(expandedGroups).filter(key => expandedGroups[key])}
          onChange={(keys) => {
            const newExpanded = {};
            keys.forEach(key => {
              newExpanded[key] = true;
            });
            Object.keys(expandedGroups).forEach(key => {
              if (!newExpanded[key]) {
                newExpanded[key] = false;
              }
            });
            setExpandedGroups(newExpanded);
          }}
          expandIconPosition="end"
          ghost
        >
          {groupedProducts.map((group, index) => {
            const isExpanded = expandedGroups[group.groupId] || false;
            const groupStockData = groupStocks[group.groupId];
            const isLoading = groupLoading[group.groupId] || false;
            const isRefreshing = refreshingGroup === group.groupId;
            
            return (
              <Panel
                key={group.groupId}
                header={
                    <div 
                        style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            width: '100%',
                            paddingRight: '20px',
                            // Add border based on expiry status
                            border: (() => {
                                const status = groupExpiryStatus[group.groupId];
                                if (status) {
                                    if (status.severity === 2) {
                                        return '2px solid #ff4d4f';
                                    } else if (status.severity === 1) {
                                        return '2px solid #faad14';
                                    }
                                }
                                return '2px solid transparent';
                            })(),
                            borderRadius: '8px',
                            padding: '8px 12px',
                            backgroundColor: (() => {
                                const status = groupExpiryStatus[group.groupId];
                                if (status) {
                                    if (status.severity === 2) {
                                        return '#fff1f0';
                                    } else if (status.severity === 1) {
                                        return '#fffbe6';
                                    }
                                }
                                return 'transparent';
                            })(),
                            transition: 'all 0.3s ease'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            // Check expiry status when expanding
                            if (!isExpanded && groupExpiryStatus[group.groupId] === undefined) {
                                checkGroupExpiryStatus(group.groupId, group);
                            }
                            handleGroupExpand(group.groupId, !isExpanded);
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <div style={{ 
                                backgroundColor: (() => {
                                    const status = groupExpiryStatus[group.groupId];
                                    if (status) {
                                        if (status.severity === 2) return '#ff4d4f';
                                        if (status.severity === 1) return '#faad14';
                                    }
                                    return '#722ed1';
                                })(),
                                color: 'white',
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '12px',
                                fontWeight: 'bold',
                                fontSize: '16px'
                            }}>
                                {index + 1}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                                    <Title level={5} style={{ margin: 0, marginRight: '12px' }}>
                                        {group.groupProductName || group.baseName}
                                    </Title>
                                    
                                    {/* Expiry Status Badges */}
                                    {groupExpiryStatus[group.groupId] && (
                                        <>
                                            {groupExpiryStatus[group.groupId].severity === 2 && (
                                                <Tag color="red" style={{ marginLeft: '4px' }}>
                                                    ⚠️ EXPIRED
                                                </Tag>
                                            )}
                                            {groupExpiryStatus[group.groupId].severity === 1 && (
                                                <Tag color="orange" style={{ marginLeft: '4px' }}>
                                                    ⚠️ Expiring Soon
                                                </Tag>
                                            )}
                                            {groupExpiryStatus[group.groupId].severity === 0 && 
                                            groupExpiryStatus[group.groupId].totalValidStock > 0 && (
                                                <Tag color="green" style={{ marginLeft: '4px' }}>
                                                    ✓ In Stock
                                                </Tag>
                                            )}
                                        </>
                                    )}
                                    
                                    {groupExpiryLoading[group.groupId] && (
                                        <Spin size="small" style={{ marginLeft: '8px' }} />
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Text type="secondary">
                                        <ShoppingOutlined style={{ marginRight: '4px' }} />
                                        {group.totalQuantity.toFixed(2)} total units
                                    </Text>
                                    <Text strong style={{ color: '#52c41a' }}>
                                        <DollarOutlined style={{ marginRight: '4px' }} />
                                        {CURRENCY}{group.totalPrice.toFixed(2)} total value
                                    </Text>
                                    
                                    {/* Show expiry date if available */}
                                    {groupExpiryStatus[group.groupId]?.earliestExpiryDate && (
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            <CalendarOutlined style={{ marginRight: '4px' }} />
                                            Earliest expiry: {formatDate(groupExpiryStatus[group.groupId].earliestExpiryDate)}
                                            {(() => {
                                                const days = calculateDaysDiff(groupExpiryStatus[group.groupId].earliestExpiryDate);
                                                if (days !== null) {
                                                    return ` (${days} days)`;
                                                }
                                                return '';
                                            })()}
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Tag color={isExpanded ? "green" : "default"}>
                                {isExpanded ? "Expanded" : "Click to expand"}
                            </Tag>
                            <DownOutlined 
                                rotate={isExpanded ? 180 : 0} 
                                style={{ 
                                    marginLeft: '12px', 
                                    transition: 'transform 0.3s',
                                    color: '#722ed1'
                                }} 
                            />
                        </div>
                    </div>
                }
                showArrow={false}
            >

                {isLoading || isRefreshing ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: '16px' }}>
                      {isRefreshing ? 'Refreshing stock data...' : 'Loading stock items for all variants...'}
                    </p>
                  </div>
                ) : (
                  <>
                    {groupStockData?.stocks && groupStockData.stocks.length > 0 ? (
                      <div>
                      
                        <Table
                          columns={getGroupStockColumns(group)}
                          dataSource={groupStockData.stocks.filter(
                            stock => parseFloat(stock.unitOfMeasurement1 || 0) >= 0.01
                          )}
                          rowKey="id"
                          pagination={{
                            current: groupStockData.currentPage || 1,
                            pageSize: stocksPageSize,
                            total: groupStockData.total || 0,
                            showSizeChanger: false,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            onChange: (page) => handleProductPageChange(group.groupId, page)
                          }}
                          scroll={{ x: 1200 }}
                          size="small"
                          bordered
                        />
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
                        <ShoppingOutlined style={{ fontSize: '32px', marginBottom: '12px' }} />
                        <p>No stock items found for any variant in this group</p>
                       
                        
                      </div>
                    )}
                  </>
                )}
              </Panel>
            );
          })}
        </Collapse>
      )}
    </Modal>
  );
};

export default StorageDetailModal;